import { TypedDocumentNode, gql } from '@apollo/client';
import { faGavel } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Block } from '@sorare/core/src/atoms/layout/Block';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, DisplayS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { Nickname } from '@sorare/core/src/components/user/Nickname';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { FOOTBALL_CLUB_SHOW_CARDS } from '@sorare/core/src/constants/routes';
import { isType } from '@sorare/core/src/gql';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PaymentBoxTwoColumnsLayout } from 'components/buyActions/PaymentBox';
import CardOverview from 'components/buyActions/PaymentBox/CardOverview';

import { EndedAuctionPaymentBox_tokenAuction } from './__generated__/index.graphql';

export const messages = defineMessages({
  bid: { id: 'BidField.bid', defaultMessage: 'Bid' },
  confirmBid: { id: 'BidField.confirmBid', defaultMessage: 'Confirm bid' },
  increaseBid: { id: 'BidField.increaseBid', defaultMessage: 'Increase bid' },
  autoBid: { id: 'BidPaymentModal.autoBid', defaultMessage: 'Set max bid' },
});

const Wrapper = styled(Vertical).attrs({ gap: 4 })`
  margin: auto 0;
  @media ${laptopAndAbove} {
    padding-bottom: calc(15 * var(--unit));
  }
`;

export const EndedAuctionPaymentBox = ({
  auction,
  onClose,
}: {
  auction: EndedAuctionPaymentBox_tokenAuction;
  onClose: () => void;
}) => {
  const track = useEvents();
  const { anyCards, bestBid } = auction;

  const to =
    auction.anyCards.length > 1
      ? generateSportPath(FOOTBALL_CLUB_SHOW_CARDS, {
          params: {
            slug: anyCards[0].anyTeam.slug,
          },
          sport: anyCards[0].sport,
        })
      : `${generateSportPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
          params: {
            slug: anyCards[0].anyPlayer.slug,
          },
          sport: anyCards[0].sport,
        })}?sale=true&rarity=${anyCards[0].rarityTyped}`;

  const onClick = () => {
    track('Click See Other Listings From Ended Auction', {});
  };

  return (
    <PaymentBoxTwoColumnsLayout
      title={null}
      tokenPreview={
        <CardOverview
          cards={anyCards}
          soldOnPrimary={false}
          showRecentSales={false}
        />
      }
      onClose={onClose}
    >
      <Wrapper gap={4} center>
        <FontAwesomeIcon size="4x" icon={faGavel} />
        <Vertical gap={0} center>
          <DisplayS>
            <FormattedMessage
              id="EndedAuctionPaymentBox.title"
              defaultMessage="Auction has just ended"
            />
          </DisplayS>
          <BodyS color="var(--c-nd-600-opaque)">
            <FormattedMessage
              id="EndedAuctionPaymentBox.subtitle"
              defaultMessage="Better luck next time!"
            />
          </BodyS>
        </Vertical>
        {bestBid?.bidder && isType(bestBid?.bidder, 'User') && (
          <Block>
            <Horizontal gap={2} center>
              <BodyM color="var(--c-nd-600-opaque)">
                <FormattedMessage
                  id="EndedAuctionPaymentBox.wonBy"
                  defaultMessage="Won By"
                />
              </BodyM>
              <Horizontal>
                <ActiveUserAvatar
                  rounded
                  user={bestBid?.bidder}
                  variant="medium"
                />
                <Vertical gap={0}>
                  <BodyM bold>
                    <Ellipsis>
                      <Nickname user={bestBid?.bidder} />
                    </Ellipsis>
                  </BodyM>
                  <AmountWithConversion monetaryAmount={bestBid.amounts} />
                </Vertical>
              </Horizontal>
            </Horizontal>
          </Block>
        )}
        <Button onClick={onClick} size="large" to={to}>
          <FormattedMessage
            id="EndedAuctionPaymentBox.cta"
            defaultMessage="Browse other listings"
          />
        </Button>
      </Wrapper>
    </PaymentBoxTwoColumnsLayout>
  );
};

EndedAuctionPaymentBox.fragments = {
  tokenAuction: gql`
    fragment EndedAuctionPaymentBox_tokenAuction on TokenAuction {
      id
      anyCards {
        slug
        sport
        anyPlayer {
          slug
        }
        anyTeam {
          slug
        }
        ...CardOverview_anyCard
      }
      bestBid {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        bidder {
          ... on User {
            slug
            ...ActiveUserAvatar_user
            ...Nickname_publicUserInfoInterface
          }
        }
      }
    }
    ${CardOverview.fragments.anyCard}
    ${ActiveUserAvatar.fragments.user}
    ${Nickname.fragments.user}
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<EndedAuctionPaymentBox_tokenAuction>,
};
